import { lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";

// Static: auth wrappers, layout shells, and route guards must be eagerly loaded
import { ProtectedRoute } from "@/components/protected-route";
import { MemberProtectedRoute } from "@/components/member-protected-route";
import { AppShell } from "./layout/AppShell";
import MemberPortalLayout from "@/pages/member/MemberPortalLayout";
import { ReportsGuard } from "@/components/reports/ReportsGuard";
import { MarketingGuard } from "@/components/marketing/MarketingGuard";
import { InventoryGuard } from "@/components/inventory/InventoryGuard";
import { MaintenanceGuard } from "@/components/maintenance/MaintenanceGuard";
import { StockGuard } from "@/components/pos/StockGuard";
import { PermissionGuard } from "@/components/PermissionGuard";

// Lazy page components — each loads only when its route is visited
const CheckInPage = lazy(() => import("@/pages/checkin/CheckInPage"));
const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/login/ForgotPasswordPage"));
const LoginPage = lazy(() => import("@/pages/login/LoginPage"));
const SetPasswordPage = lazy(() => import("@/pages/login/SetPasswordPage"));
const TwoFactorPromptPage = lazy(() => import("@/pages/login/TwoFactorPromptPage"));
const ResetTwoFactorPage = lazy(() => import("@/pages/login/ResetTwoFactorPage"));
const ForcedEnrolPage = lazy(() => import("@/pages/login/ForcedEnrolPage"));
const ForcedEnrolRecoveryPage = lazy(() => import("@/pages/login/ForcedEnrolRecoveryPage"));
const MedicalAccessLogPage = lazy(() => import("@/pages/members/MedicalAccessLogPage"));
const MemberCreatePage = lazy(() => import("@/pages/members/MemberCreatePage"));
const MemberDetailPage = lazy(() => import("@/pages/members/MemberDetailPage"));
const MembersListPage = lazy(() => import("@/pages/members/MembersListPage"));
const PendingAdjustmentsPage = lazy(() => import("@/pages/members/PendingAdjustmentsPage"));
const OnboardingWizardPage = lazy(() => import("@/pages/members/onboarding/OnboardingWizardPage"));
const CashbookPage = lazy(() => import("@/pages/cashbook/CashbookPage"));
const TakePaymentPage = lazy(() => import("@/pages/payments/TakePaymentPage"));
const PaymentReturnPage = lazy(() => import("@/pages/payments/PaymentReturnPage"));
const AuditLogPage = lazy(() => import("@/pages/settings/AuditLogPage"));
const PreferencesPage = lazy(() => import("@/pages/settings/PreferencesPage"));
const AuditRetentionPage = lazy(() => import("@/pages/settings/AuditRetentionPage"));
const PaymentGatewaysPage = lazy(() => import("@/pages/settings/PaymentGatewaysPage"));
const BranchSettingsPage = lazy(() => import("@/pages/settings/BranchSettingsPage"));
const PrinterConfigPage = lazy(() => import("@/pages/settings/PrinterConfigPage"));
const ClassPolicySettingsPage = lazy(() => import("@/pages/settings/ClassPolicySettingsPage"));
const ClassAttendanceSettingsPage = lazy(() => import("@/pages/settings/ClassAttendanceSettingsPage"));
const ReferralProgramSettingsPage = lazy(() => import("@/pages/settings/ReferralProgramSettingsPage"));
const ReferralLeaderboardPage = lazy(() => import("@/pages/referrals/ReferralLeaderboardPage"));
const ReferralAdminPage = lazy(() => import("@/pages/referrals/ReferralAdminPage"));
const NotificationPreferencesPage = lazy(() => import("@/pages/settings/notifications/NotificationPreferencesPage"));
const OrganizationCatalogPage = lazy(() => import("@/pages/settings/OrganizationCatalogPage"));
const PlanCatalogPage = lazy(() => import("@/pages/settings/PlanCatalogPage"));
const PromotionCatalogPage = lazy(() => import("@/pages/settings/PromotionCatalogPage"));
const DynamicPricingRulesPage = lazy(() => import("@/pages/DynamicPricing/RulesPage"));
const RoleDetailPage = lazy(() => import("@/pages/settings/rbac/RoleDetailPage"));
const RoleNewPage = lazy(() => import("@/pages/settings/rbac/RoleNewPage"));
const RolesListPage = lazy(() => import("@/pages/settings/rbac/RolesListPage"));
const UserDetailPage = lazy(() => import("@/pages/settings/rbac/UserDetailPage"));
const UserNewPage = lazy(() => import("@/pages/settings/rbac/UserNewPage"));
const UsersListPage = lazy(() => import("@/pages/settings/rbac/UsersListPage"));
const SettingsHomePage = lazy(() => import("@/pages/settings/SettingsHomePage"));
const TwoFactorSettingsPage = lazy(() => import("@/pages/settings/security/TwoFactorSettingsPage"));
const TwoFactorEnrolPage = lazy(() => import("@/pages/settings/security/TwoFactorEnrolPage"));
const RecoveryCodesPage = lazy(() => import("@/pages/settings/security/RecoveryCodesPage"));
const TwoFactorResetQueuePage = lazy(() => import("@/pages/settings/security/TwoFactorResetQueuePage"));
const SecurityPolicyPage = lazy(() => import("@/pages/settings/security/SecurityPolicyPage"));
const ClassTemplatesPage = lazy(() => import("@/pages/classes/ClassTemplatesPage"));
const ClassOccurrencesPage = lazy(() => import("@/pages/classes/ClassOccurrencesPage"));
const ClassPenaltiesPage = lazy(() => import("@/pages/classes/ClassPenaltiesPage"));
const ClassSchedulesPage = lazy(() => import("@/pages/classes/ClassSchedulesPage"));
const BookingCalendarPage = lazy(() => import("@/pages/classes/BookingCalendarPage"));
const DropInPurchasePage = lazy(() => import("@/pages/classes/DropInPurchasePage"));
const ClassHolidaysSettingsPage = lazy(() => import("@/pages/settings/ClassHolidaysSettingsPage"));
const StaffListPage = lazy(() => import("@/pages/staff/StaffListPage"));
const LeaveTypesPage = lazy(() => import("@/pages/leave/LeaveTypesPage"));
const LeaveRequestsPage = lazy(() => import("@/pages/leave/LeaveRequestsPage"));
const LeaveApprovalQueuePage = lazy(() => import("@/pages/leave/LeaveApprovalQueuePage"));
const StaffCreatePage = lazy(() => import("@/pages/staff/StaffCreatePage"));
const StaffEditPage = lazy(() => import("@/pages/staff/StaffEditPage"));
const StaffDetailPage = lazy(() => import("@/pages/staff/StaffDetailPage"));
const TodaysSessions = lazy(() => import("@/pages/staff/TodaysSessions"));
const ClockPage = lazy(() => import("@/pages/clock/ClockPage"));
const TimesheetsPage = lazy(() => import("@/pages/timesheets/TimesheetsPage"));
const TrainerListPage = lazy(() => import("@/pages/trainers/TrainerListPage"));
const TrainerProfilePage = lazy(() => import("@/pages/trainers/TrainerProfilePage"));
const TrainerProfileEditPage = lazy(() => import("@/pages/trainers/TrainerProfileEditPage"));
const TrainerKpiDashboard = lazy(() =>
  import("@/pages/trainers/TrainerKpiDashboard").then((m) => ({ default: m.TrainerKpiDashboard })),
);
const FilteredPtBookings = lazy(() => import("@/pages/staff/FilteredPtBookings"));
const PtBookingNewPage = lazy(() => import("@/pages/staff/PtBookingNewPage"));
const SharedSlotPage = lazy(() => import("@/pages/staff/SharedSlotPage"));
const PTSessionTypePage = lazy(() => import("@/pages/settings/PTSessionTypePage"));
const PtReassignSettingsPage = lazy(() => import("@/pages/settings/PtReassignSettingsPage"));
const PayrollListPage = lazy(() => import("@/pages/payroll/PayrollListPage"));
const PayrollDetailPage = lazy(() => import("@/pages/payroll/PayrollDetailPage"));
const RevenueByPeriodPage = lazy(() => import("@/pages/Reports/RevenueByPeriod"));
const CheckinLogPage = lazy(() => import("@/pages/Reports/CheckinLogPage"));
const MembersByStatusPage = lazy(() => import("@/pages/Reports/MembersByStatus"));
const DuesAgingPage = lazy(() => import("@/pages/Reports/DuesAging"));
const PayrollSummaryPage = lazy(() => import("@/pages/Reports/PayrollSummary"));
const LeaveReportPage = lazy(() => import("@/pages/Reports/LeaveReportPage"));
const PlanMixPage = lazy(() => import("@/pages/Reports/PlanMix"));
const PromoRedemptionPage = lazy(() => import("@/pages/Reports/PromoRedemption"));
const LowStockReportPage = lazy(() => import("@/pages/Reports/LowStockReport"));
const FailedRenewalsReportPage = lazy(() => import("@/pages/Reports/FailedRenewalsReport"));
const KpiComparePage = lazy(() => import("@/pages/Reports/KpiComparePage"));
const CohortAnalysisPage = lazy(() => import("@/pages/Reports/CohortAnalysis"));
const ReportBuilderPage = lazy(() => import("@/pages/Reports/ReportBuilderPage"));
const SavedReportsPage = lazy(() => import("@/pages/Reports/SavedReportsPage"));
const ScheduledReportsPage = lazy(() =>
  import("@/pages/Reports/ScheduledReportsPage").then((m) => ({ default: m.ScheduledReportsPage })),
);
const LeaderboardsPage = lazy(() => import("@/pages/Reports/LeaderboardsPage"));
const BadgeCatalogPage = lazy(() => import("@/pages/gamification/BadgeCatalogPage"));
const AccessDuesSettingsPage = lazy(() => import("@/pages/settings/AccessDuesSettingsPage"));
const InventoryCOGSSettingsPage = lazy(() => import("@/pages/settings/InventoryCOGSSettingsPage"));
const PTCommissionSettingsPage = lazy(() => import("@/pages/settings/PTCommissionSettingsPage"));
const ReconciliationPOSSettingsPage = lazy(() => import("@/pages/settings/ReconciliationPOSSettingsPage"));
const DiscountLimitsSettingsPage = lazy(() => import("@/pages/settings/DiscountLimitsSettingsPage"));
const CommunitySettingsPage = lazy(() => import("@/pages/settings/CommunitySettingsPage"));
const MemberPolicySettingsPage = lazy(() => import("@/pages/settings/MemberPolicySettingsPage"));
const LocalizationSettingsPage = lazy(() => import("@/pages/settings/LocalizationSettingsPage"));
const SelfRegistrationSettingsPage = lazy(() => import("@/pages/settings/SelfRegistrationSettingsPage"));
const ApprovalSettingsPage = lazy(() => import("@/pages/settings/ApprovalSettingsPage"));
const GamifLeaderboardPage = lazy(() => import("@/pages/gamification/LeaderboardPage"));
const PaymentReconciliationPage = lazy(() => import("@/pages/Reports/PaymentReconciliation"));
const POSPage = lazy(() => import("@/pages/pos/POSPage"));
const ShiftHistoryPage = lazy(() => import("@/pages/pos/ShiftHistoryPage"));
const SalesHistoryPage = lazy(() => import("@/pages/pos/SalesHistoryPage"));
const ProductCatalogPage = lazy(() => import("@/pages/settings/ProductCatalogPage"));
const StoreManagementPage = lazy(() => import("@/pages/settings/StoreManagementPage"));
const CampaignListPage = lazy(() => import("@/pages/marketing/CampaignListPage"));
const CampaignBuilderPage = lazy(() => import("@/pages/marketing/CampaignBuilderPage"));
const NotificationHistoryPage = lazy(() => import("@/pages/notifications/NotificationHistoryPage"));
const IncidentListPage = lazy(() => import("@/pages/ops/IncidentListPage"));
const IncidentFormPage = lazy(() => import("@/pages/ops/IncidentFormPage"));
const IncidentDetailPage = lazy(() => import("@/pages/ops/IncidentDetailPage"));
const LostAndFoundPage = lazy(() => import("@/pages/ops/LostAndFoundPage"));
const DLQPage = lazy(() => import("@/pages/ops/DLQPage"));
const JobsPage = lazy(() => import("@/pages/ops/JobsPage"));
const TrainerHomePage = lazy(() => import("@/pages/trainer/TrainerHomePage"));
const TrainerAvailabilityPage = lazy(() => import("@/pages/trainer/TrainerAvailabilityPage"));
const TrainerClassRosterPage = lazy(() => import("@/pages/trainer/TrainerClassRosterPage"));
const LeadsListPage = lazy(() => import("@/pages/leads/LeadsListPage"));
const LeadDetailPage = lazy(() => import("@/pages/leads/LeadDetailPage"));
const TrialsWorklistPage = lazy(() => import("@/pages/leads/TrialsWorklistPage"));
const LeadBoardPage = lazy(() => import("@/pages/leads/LeadBoardPage"));
const LeadCrmSettingsPage = lazy(() => import("@/pages/leads/LeadCrmSettingsPage"));
const TrialTemplatesPage = lazy(() => import("@/pages/settings/TrialTemplatesPage"));
const RetentionPage = lazy(() => import("@/pages/retention/RetentionPage"));
const NpsFollowUpsPage = lazy(() => import("@/pages/nps/NpsFollowUpsPage"));
const NpsResponsesPage = lazy(() => import("@/pages/nps/NpsResponsesPage"));
const NpsConfigPage = lazy(() => import("@/pages/nps/NpsConfigPage"));
const GettingStartedPage = lazy(() => import("@/pages/getting-started/GettingStartedPage"));
const DigestSettingsPage = lazy(() => import("@/pages/settings/DigestSettingsPage"));
const DigestUnsubscribePage = lazy(() => import("@/pages/settings/DigestUnsubscribePage"));
const CorporateDashboardPage = lazy(() => import("@/pages/memberships/CorporateDashboardPage"));
const SellMembershipPage = lazy(() => import("@/pages/memberships/SellMembershipPage"));
const MembershipsHubPage = lazy(() => import("@/pages/memberships/MembershipsHubPage"));
const CorporateRenewalPipelinePage = lazy(() => import("@/pages/Reports/CorporateRenewalPipelinePage"));
const ProfitLossPage = lazy(() => import("@/pages/Reports/ProfitLossPage"));
const VatReportPage = lazy(() => import("@/pages/Reports/VatReportPage"));
const SelfServiceReviewQueuePage = lazy(() => import("@/pages/subscriptions/SelfServiceReviewQueuePage"));
const SuppliersPage = lazy(() => import("@/pages/inventory/SuppliersPage"));
const PurchaseOrdersPage = lazy(() => import("@/pages/inventory/PurchaseOrdersPage"));
const PredictiveReorderPage = lazy(() => import("@/pages/inventory/PredictiveReorderPage"));
const DebitNotesPage = lazy(() => import("@/pages/inventory/DebitNotesPage"));
const DebitNoteCreatePage = lazy(() => import("@/pages/inventory/DebitNoteCreatePage"));
const POCreatePage = lazy(() => import("@/pages/inventory/POCreatePage"));
const PODetailPage = lazy(() => import("@/pages/inventory/PODetailPage"));
const POReceivePage = lazy(() => import("@/pages/inventory/POReceivePage"));
const StockTakesPage = lazy(() => import("@/pages/inventory/StockTakesPage"));
const StockTakeDetailPage = lazy(() => import("@/pages/inventory/StockTakeDetailPage"));
const SupplierInvoicesPage = lazy(() => import("@/pages/inventory/SupplierInvoicesPage"));
const StockManagementPage = lazy(() => import("@/pages/pos/StockManagementPage"));
const AssetRegisterPage = lazy(() => import("@/pages/maintenance/AssetRegisterPage"));
const AssetDetailPage = lazy(() => import("@/pages/maintenance/AssetDetailPage"));
const AssetFormPage = lazy(() => import("@/pages/maintenance/AssetFormPage"));
const AssetCsvImportPage = lazy(() => import("@/pages/maintenance/AssetCsvImportPage"));
const ZoneListPage = lazy(() => import("@/pages/maintenance/ZoneListPage"));
const ZoneFormPage = lazy(() => import("@/pages/maintenance/ZoneFormPage"));
const ExerciseLibraryPage = lazy(() => import("@/pages/exercises/ExerciseLibraryPage"));
const ExerciseSwapCurationPage = lazy(() => import("@/pages/settings/ExerciseSwapCurationPage"));
const ExerciseDetailPage = lazy(() => import("@/pages/exercises/ExerciseDetailPage"));
const WorkoutListPage = lazy(() => import("@/pages/workouts/WorkoutListPage"));
const WorkoutBuilderPage = lazy(() => import("@/pages/workouts/WorkoutBuilderPage"));
const ProgramListPage = lazy(() => import("@/pages/programs/ProgramListPage"));
const ProgramBuilderPage = lazy(() => import("@/pages/programs/ProgramBuilderPage"));
const ClientsProgramsPage = lazy(() => import("@/pages/programs/ClientsProgramsPage"));
const AssignProgramPage = lazy(() => import("@/pages/programs/AssignProgramPage"));
const FoodListPage = lazy(() => import("@/pages/nutrition/FoodListPage"));
const MealTemplatesPage = lazy(() => import("@/pages/nutrition/MealTemplatesPage"));
const NutritionPlanListPage = lazy(() => import("@/pages/nutrition/NutritionPlanListPage"));
const NutritionPlanBuilderPage = lazy(() => import("@/pages/nutrition/NutritionPlanBuilderPage"));
const DevicesPage = lazy(() => import("@/pages/access/DevicesPage"));
const BioDevicesPage = lazy(() => import("@/pages/settings/devices/DevicesPage"));
const BioDeviceDetailPage = lazy(() => import("@/pages/settings/devices/DeviceDetailPage"));
const AccessZonesPage = lazy(() => import("@/pages/settings/AccessZonesPage"));
const KioskDevicesPage = lazy(() => import("@/pages/settings/kiosk-devices/KioskDevicesPage"));
const LiveFeedPage = lazy(() => import("@/pages/checkin/LiveFeedPage"));
const AccessAnalyticsPage = lazy(() => import("@/pages/analytics/AccessAnalyticsPage"));
const MemberLoginPage = lazy(() => import("@/pages/member/MemberLoginPage"));
const MemberForgotPasswordPage = lazy(() => import("@/pages/member/MemberForgotPasswordPage"));
const MemberSetPasswordPage = lazy(() => import("@/pages/member/MemberSetPasswordPage"));
const MemberHomePage = lazy(() => import("@/pages/member/MemberHomePage"));
const MemberClassesPage = lazy(() => import("@/pages/member/MemberClassesPage"));
const MemberWorkoutPage = lazy(() => import("@/pages/member/MemberWorkoutPage"));
const MemberNutritionPage = lazy(() => import("@/pages/member/MemberNutritionPage"));
const MemberDocumentsPage = lazy(() => import("@/pages/member/MemberDocumentsPage"));
const MemberPrivacyPage = lazy(() => import("@/pages/member/MemberPrivacyPage"));
const MemberConsentPage = lazy(() => import("@/pages/member/MemberConsentPage"));
const MemberAchievementsPage = lazy(() => import("@/pages/member/MemberAchievementsPage"));
const MemberAccessQrPage = lazy(() => import("@/pages/member/MemberAccessQrPage"));
const MemberPtPage = lazy(() => import("@/pages/member/MemberPtPage"));
const MemberWalletPage = lazy(() => import("@/pages/member/MemberWalletPage"));
const DocumentVisibilitySettingsPage = lazy(() => import("@/pages/settings/DocumentVisibilitySettingsPage"));
const KioskClassesPage = lazy(() => import("@/pages/kiosk/KioskClassesPage"));
const KioskShell = lazy(() => import("@/pages/kiosk/KioskShell"));
const KioskPaymentReturnPage = lazy(() => import("@/pages/kiosk/KioskPaymentReturnPage"));
const NpsSurveyPage = lazy(() => import("@/pages/surveys/NpsSurveyPage"));
const GymPickerPage = lazy(() => import("@/pages/public/GymPickerPage"));
const SelfRegWizardPage = lazy(() => import("@/pages/public/SelfRegWizardPage"));
const TrialBookingPage = lazy(() => import("@/pages/trial/TrialBookingPage"));
const SelfRegistrationQueuePage = lazy(() => import("@/pages/members/SelfRegistrationQueuePage"));
const AccountingMappingPage = lazy(() => import("@/pages/settings/AccountingMappingPage"));
const AccountingExportsPage = lazy(() => import("@/pages/Reports/AccountingExportsPage"));
const DeferredRevenuePage = lazy(() => import("@/pages/Reports/DeferredRevenuePage"));
const LockedAccountingPeriodsPage = lazy(() => import("@/pages/settings/LockedAccountingPeriodsPage"));
const DeveloperPortalPage = lazy(() => import("@/pages/settings/developer/DeveloperPortalPage"));
const BrandingSettingsPage = lazy(() => import("@/pages/settings/BrandingSettingsPage"));
const AboutPage = lazy(() => import("@/pages/settings/AboutPage"));
const InductionTemplatePage = lazy(() => import("@/pages/settings/InductionTemplatePage"));
const AssessmentTemplatePage = lazy(() => import("@/pages/settings/AssessmentTemplatePage"));
const NotificationTemplatesPage = lazy(() => import("@/pages/settings/notifications/NotificationTemplatesPage"));
const EmailSettingsPage = lazy(() => import("@/pages/settings/notifications/EmailSettingsPage"));
const SmsSettingsPage = lazy(() => import("@/pages/settings/notifications/SmsSettingsPage"));
const WhatsAppSettingsPage = lazy(() => import("@/pages/settings/notifications/WhatsAppSettingsPage"));
const ReviewModerationPage = lazy(() => import("@/pages/staff/ReviewModerationPage"));
const ReviewQuestionsPage = lazy(() => import("@/pages/staff/ReviewQuestionsPage"));
const PtRequestsPage = lazy(() => import("@/pages/staff/PtRequestsPage"));
const ClassRequestsPage = lazy(() => import("@/pages/staff/ClassRequestsPage"));
const NotificationCenterPage = lazy(() => import("@/pages/staff/NotificationCenterPage"));
const DailySalesReportPage = lazy(() => import("@/pages/sales/DailySalesReportPage"));
const PaymentsReceivedPage = lazy(() => import("@/pages/sales/PaymentsReceivedPage"));
const CreditNotesPage = lazy(() => import("@/pages/sales/CreditNotesPage"));
const CreditNoteCreatePage = lazy(() => import("@/pages/sales/CreditNoteCreatePage"));
const FinancePtBookingsPage = lazy(() => import("@/pages/staff/FinancePtBookingsPage"));

export const router = createBrowserRouter(
  [
    { path: "/login", element: <LoginPage /> },
    // GYM-47 public routes
    { path: "/forgot-password", element: <ForgotPasswordPage /> },
    { path: "/set-password", element: <SetPasswordPage /> },
    // GYM-533: 2FA public routes (no auth required)
    { path: "/2fa/verify", element: <TwoFactorPromptPage /> },
    { path: "/2fa/reset", element: <ResetTwoFactorPage /> },
    // GYM-1430: forced-enrolment public routes (token-gated, no session yet)
    { path: "/2fa/forced-enrol", element: <ForcedEnrolPage /> },
    {
      path: "/2fa/forced-enrol/recovery",
      element: <ForcedEnrolRecoveryPage />,
    },
    // GYM-539: public unsubscribe page (no auth — reached from digest email footer)
    { path: "/digest/unsubscribe", element: <DigestUnsubscribePage /> },
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: <DashboardPage /> },

        // GYM-237/238/239/240: Members module
        {
          element: <PermissionGuard permission="members.read" />,
          children: [
            { path: "members", element: <MembersListPage /> },
            { path: "members/new", element: <MemberCreatePage /> },
            {
              path: "members/adjustments/pending",
              element: <PendingAdjustmentsPage />,
            },
            // GYM-1681: self-registration approval queue
            {
              path: "members/self-reg-queue",
              element: <SelfRegistrationQueuePage />,
            },
            // Static segments must precede :memberId to prevent them matching as IDs.
            {
              path: "members/duplicates",
              element: <Navigate to="/members" replace />,
            },
            { path: "members/import", element: <Navigate to="/members" replace /> },
            // GYM-486: Onboarding wizard
            { path: "members/onboard", element: <OnboardingWizardPage /> },
            {
              path: "members/onboard/:draftId/:step",
              element: <OnboardingWizardPage />,
            },
            { path: "members/:memberId", element: <MemberDetailPage /> },
            { path: "members/:memberId/edit", element: <MemberCreatePage /> },
            {
              path: "members/:memberId/medical-access-log",
              element: <MedicalAccessLogPage />,
            },
            // GYM-372: notification preference matrix
            {
              path: "members/:memberId/notification-preferences",
              element: <NotificationPreferencesPage />,
            },
          ],
        },

        // Memberships hub — replaces the PlaceholderPage stub.
        { path: "memberships", element: <MembershipsHubPage /> },
        // GYM-776: Corporate dashboard
        {
          path: "memberships/corporate-dashboard",
          element: <CorporateDashboardPage />,
        },
        // GYM-1140: Group-membership sell wizard
        { path: "memberships/sell", element: <SellMembershipPage /> },
        // GYM-295: reception manual / QR check-in page.
        { path: "check-in", element: <CheckInPage /> },
        // diet-plans route removed — feature not yet built; hiding from nav
        // is sufficient; old bookmarks redirect to memberships hub.
        { path: "diet-plans", element: <Navigate to="/memberships" replace /> },
        {
          path: "classes",
          element: <Navigate to="/classes/occurrences" replace />,
        },
        { path: "classes/templates", element: <ClassTemplatesPage /> },
        { path: "classes/occurrences", element: <ClassOccurrencesPage /> },
        // GYM-1493: Class schedules page
        { path: "classes/schedules", element: <ClassSchedulesPage /> },
        { path: "classes/bookings", element: <BookingCalendarPage /> },
        { path: "classes/drop-in", element: <DropInPurchasePage /> },
        { path: "classes/penalties", element: <ClassPenaltiesPage /> },
        // GYM-120: Staff module (GYM-121: separate Create/Edit pages — never shared)
        { path: "staff", element: <StaffListPage /> },
        { path: "staff/new", element: <StaffCreatePage /> },
        { path: "staff/todays-sessions", element: <TodaysSessions /> },
        // GYM-473: Trainer clock-in/out + timesheet
        { path: "clock", element: <ClockPage /> },
        { path: "timesheets", element: <TimesheetsPage /> },
        { path: "staff/attendance", element: <TimesheetsPage /> },
        // GYM-477: Filtered PT bookings (drill-down from trainer KPI tiles)
        { path: "staff/pt/bookings", element: <FilteredPtBookings /> },
        // GYM-474: New PT booking form
        { path: "staff/pt/bookings/new", element: <PtBookingNewPage /> },
        // GYM-482: Shared-slot (small-group / semi-private PT) management
        { path: "staff/pt/shared-slots", element: <SharedSlotPage /> },
        { path: "staff/:staffId", element: <StaffDetailPage /> },
        { path: "staff/:staffId/edit", element: <StaffEditPage /> },
        // GYM-120: Payroll module (GYM-122: PayrollDetailPage with sub-components)
        { path: "payroll", element: <PayrollListPage /> },
        { path: "payroll/:runId", element: <PayrollDetailPage /> },
        // Legacy redirect: /trainers → /staff
        { path: "trainers", element: <Navigate to="/staff" replace /> },
        // GYM-1394: /staff/trainers was resolving to StaffDetailPage with id="trainers" → crash
        {
          path: "staff/trainers",
          element: <Navigate to="/trainers/directory" replace />,
        },
        // GYM-471: Trainer public profile pages
        { path: "trainers/directory", element: <TrainerListPage /> },
        { path: "trainers/:trainerId", element: <TrainerProfilePage /> },
        {
          path: "trainers/:trainerId/edit",
          element: <TrainerProfileEditPage />,
        },

        // GYM-1437: Trainer review moderation queue (pt.review.moderate)
        { path: "staff/review-moderation", element: <ReviewModerationPage /> },
        { path: "staff/review-questions", element: <ReviewQuestionsPage /> },
        // PT booking approval queue
        { path: "staff/pt-requests", element: <PtRequestsPage /> },
        // Class booking approval queue
        { path: "staff/class-requests", element: <ClassRequestsPage /> },
        // Notification center
        { path: "notifications/center", element: <NotificationCenterPage /> },

        // Leave management
        { path: "leave/types", element: <LeaveTypesPage /> },
        { path: "leave/requests", element: <LeaveRequestsPage /> },
        { path: "leave/approvals", element: <LeaveApprovalQueuePage /> },

        // GYM-477: Trainer KPI Dashboard
        { path: "trainers/kpis", element: <TrainerKpiDashboard /> },
        // GYM-1398: /staff/kpis was missing — redirect to the correct KPI page
        {
          path: "staff/kpis",
          element: <Navigate to="/trainers/kpis" replace />,
        },
        {
          path: "trainers/comparison",
          element: <Navigate to="/reports/leaderboards" replace />,
        },
        // GYM-479 / GYM-896: Trainer mobile home (self-scoped today + KPIs)
        { path: "trainer/home", element: <TrainerHomePage /> },
        // GYM-899: Trainer availability edit (mobile touch UI)
        { path: "trainer/availability", element: <TrainerAvailabilityPage /> },
        // GYM-923: Trainer class roster (flippable attended/no-show, lock countdown)
        {
          path: "trainer/classes/:occurrenceId/roster",
          element: <TrainerClassRosterPage />,
        },
        // GYM-346: POS
        { path: "pos", element: <POSPage /> },
        // GYM-1572: POS Shift History
        { path: "pos/shifts", element: <ShiftHistoryPage /> },
        // POS Sales History
        { path: "pos/sales", element: <SalesHistoryPage /> },
        { path: "sales/daily-report", element: <DailySalesReportPage /> },
        { path: "sales/payments-received", element: <PaymentsReceivedPage /> },
        { path: "sales/credit-notes", element: <CreditNotesPage /> },
        { path: "sales/credit-notes/new", element: <CreditNoteCreatePage /> },
        // Finance Items hub — Finance-workspace-owned aliases
        { path: "finance/items/products", element: <ProductCatalogPage /> },
        { path: "finance/items/plans", element: <PlanCatalogPage /> },
        { path: "finance/items/classes", element: <ClassTemplatesPage /> },
        { path: "finance/items/zones", element: <ZoneListPage /> },
        { path: "finance/items/zones/new", element: <ZoneFormPage /> },
        { path: "finance/items/zones/:zoneId/edit", element: <ZoneFormPage /> },
        { path: "finance/items/pt-bookings", element: <FinancePtBookingsPage /> },
        // GYM-1441: POS Stock Management
        {
          element: <StockGuard />,
          children: [
            { path: "pos/stock", element: <StockManagementPage /> },
          ],
        },

        // GYM-103: payments
        { path: "payments", element: <TakePaymentPage /> },
        { path: "payments/take", element: <TakePaymentPage /> },
        // GYM-1224 / 537-F.4: shared hosted-checkout return page (PayTabs + Telr)
        {
          path: "payments/return/:returnStatus",
          element: <PaymentReturnPage />,
        },
        {
          path: "members/:memberId/take-payment",
          element: <TakePaymentPage />,
        },
        // GYM-130: Reports module
        {
          element: <ReportsGuard />,
          children: [
            {
              path: "reports",
              element: <Navigate to="/reports/revenue-by-period" replace />,
            },
            {
              path: "reports/checkin-log",
              element: <CheckinLogPage />,
            },
            {
              path: "reports/revenue-by-period",
              element: <RevenueByPeriodPage />,
            },
            {
              path: "reports/members-by-status",
              element: <MembersByStatusPage />,
            },
            { path: "reports/dues-aging", element: <DuesAgingPage /> },
            {
              path: "reports/payroll-summary",
              element: <PayrollSummaryPage />,
            },
            {
              path: "reports/leave-report",
              element: <LeaveReportPage />,
            },
            { path: "reports/plan-mix", element: <PlanMixPage /> },
            {
              path: "reports/promo-redemption",
              element: <PromoRedemptionPage />,
            },
            { path: "reports/low-stock", element: <LowStockReportPage /> },
            {
              path: "reports/failed-renewals",
              element: <FailedRenewalsReportPage />,
            },
            // GYM-528: multi-branch KPI comparison
            { path: "reports/compare", element: <KpiComparePage /> },
            // GYM-526: Custom report builder + saved reports
            { path: "reports/builder", element: <ReportBuilderPage /> },
            { path: "reports/saved", element: <SavedReportsPage /> },
            // GYM-529: Scheduled report delivery
            { path: "reports/schedules", element: <ScheduledReportsPage /> },
            // GYM-530: Cohort analysis
            {
              path: "reports/cohort-analysis",
              element: <CohortAnalysisPage />,
            },
            // GYM-531: Performance leaderboards
            { path: "reports/leaderboards", element: <LeaderboardsPage /> },
            // GYM-1284: Accounting GL export
            {
              path: "reports/accounting-exports",
              element: <AccountingExportsPage />,
            },
            // GYM-1434: Deferred revenue recognition schedules
            {
              path: "reports/deferred-revenue",
              element: <DeferredRevenuePage />,
            },
            // GYM-1223: Payout reconciliation
            {
              path: "reports/payout-reconciliation",
              element: <PaymentReconciliationPage />,
            },
            // GYM-776: Corporate renewal pipeline report
            {
              path: "reports/corporate-renewal-pipeline",
              element: <CorporateRenewalPipelinePage />,
            },
            { path: "reports/profit-loss", element: <ProfitLossPage /> },
            { path: "reports/vat", element: <VatReportPage /> },
          ],
        },
        {
          element: <MarketingGuard />,
          children: [
            {
              path: "marketing",
              element: <Navigate to="/marketing/campaigns" replace />,
            },
            // GYM-360: Campaign builder + list
            { path: "marketing/campaigns", element: <CampaignListPage /> },
            {
              path: "marketing/campaigns/new",
              element: <CampaignBuilderPage />,
            },
            {
              path: "marketing/campaigns/:campaignId",
              element: <CampaignBuilderPage />,
            },
          ],
        },

        // GYM-376: Notifications history
        { path: "notifications/history", element: <NotificationHistoryPage /> },

        // Operational safety: incidents + lost-and-found
        { path: "ops/incidents", element: <IncidentListPage /> },
        { path: "ops/incidents/new", element: <IncidentFormPage /> },
        { path: "ops/incidents/:incidentId", element: <IncidentDetailPage /> },
        {
          path: "ops/incidents/:incidentId/edit",
          element: <IncidentFormPage />,
        },
        { path: "ops/lost-and-found", element: <LostAndFoundPage /> },
        // GYM-541: DLQ ops dashboard (superuser-only, guard is inside the page)
        { path: "ops/dlq", element: <DLQPage /> },
        // GYM-1452: Jobs dashboard (ops.jobs.read-gated, guard is inside the page)
        { path: "ops/jobs", element: <JobsPage /> },

        // GYM-513: Retention work-queue (retention.savelist.view)
        { path: "retention", element: <RetentionPage /> },
        // GYM-1428: NPS staff pages (feedback.nps.read / feedback.nps.config.manage)
        { path: "nps/followups", element: <NpsFollowUpsPage /> },
        { path: "nps/responses", element: <NpsResponsesPage /> },
        { path: "settings/nps", element: <NpsConfigPage /> },
        // GYM-1417: Self-service review queue (subscriptions.selfservice.review)
        {
          path: "subscriptions/review-queue",
          element: <SelfServiceReviewQueuePage />,
        },

        // Backward-compat redirects for the pre-GYM-58 nav. Anyone with a
        // bookmarked old path lands on the closest new sibling instead of a
        // 404. Map: plans → memberships, billing/cashbook → payments, staff
        // → trainers. ``replace`` so the old path doesn't sit in browser history.
        { path: "plans", element: <Navigate to="/memberships" replace /> },
        { path: "billing", element: <Navigate to="/payments" replace /> },
        // GYM-101: cashbook page (daily cash reconciliation)
        { path: "cashbook", element: <CashbookPage /> },

        // Settings hub — RBAC pages live under /settings/* because Django serves
        // /admin/* (the Django admin). The hub page lists the available
        // sub-areas; Sprint 1 ships Roles + Users.
        { path: "settings", element: <SettingsHomePage /> },
        { path: "settings/preferences", element: <PreferencesPage /> },
        { path: "settings/roles", element: <RolesListPage /> },
        { path: "settings/roles/new", element: <RoleNewPage /> },
        { path: "settings/roles/:roleId", element: <RoleDetailPage /> },
        { path: "settings/users", element: <UsersListPage /> },
        { path: "settings/users/new", element: <UserNewPage /> },
        { path: "settings/users/:userId", element: <UserDetailPage /> },
        { path: "settings/audit", element: <AuditLogPage /> },
        { path: "settings/audit-retention", element: <AuditRetentionPage /> },
        { path: "settings/branches", element: <BranchSettingsPage /> },
        { path: "settings/plans", element: <PlanCatalogPage /> },
        // GYM-482: PT session-type configuration (manager view)
        { path: "settings/pt-session-types", element: <PTSessionTypePage /> },
        {
          path: "settings/organizations",
          element: <OrganizationCatalogPage />,
        },
        // GYM-1392: redirect all legacy/tested corporate org paths to settings/organizations
        {
          path: "settings/corporate",
          element: <Navigate to="/settings/organizations" replace />,
        },
        {
          path: "settings/groups",
          element: <Navigate to="/settings/organizations" replace />,
        },
        {
          path: "corporate",
          element: <Navigate to="/settings/organizations" replace />,
        },
        {
          path: "corporate-memberships",
          element: <Navigate to="/settings/organizations" replace />,
        },
        {
          path: "memberships/corporate",
          element: <Navigate to="/settings/organizations" replace />,
        },
        { path: "settings/promotions", element: <PromotionCatalogPage /> },
        { path: "settings/dynamic-pricing", element: <DynamicPricingRulesPage /> },
{ path: "settings/stores", element: <StoreManagementPage /> },
        { path: "settings/class-policy", element: <ClassPolicySettingsPage /> },
        {
          path: "settings/class-attendance",
          element: <ClassAttendanceSettingsPage />,
        },
        // GYM-1494: Holiday calendar settings
        {
          path: "settings/class-holidays",
          element: <ClassHolidaysSettingsPage />,
        },
        { path: "settings/printer-config", element: <PrinterConfigPage /> },
        // GYM-506: Exercise swap curation
        {
          path: "settings/exercise-swaps",
          element: <ExerciseSwapCurationPage />,
        },
        // GYM-599: Inventory (suppliers, purchase orders, stock-takes)
        {
          element: <InventoryGuard />,
          children: [
            { path: "settings/suppliers", element: <SuppliersPage /> },
            {
              path: "inventory/purchase-orders",
              element: <PurchaseOrdersPage />,
            },
            {
              path: "inventory/purchase-orders/new",
              element: <POCreatePage />,
            },
            {
              path: "inventory/purchase-orders/:id",
              element: <PODetailPage />,
            },
            {
              path: "inventory/purchase-orders/:id/receive",
              element: <POReceivePage />,
            },
            { path: "inventory/stock-takes", element: <StockTakesPage /> },
            { path: "inventory/stock-takes/:id", element: <StockTakeDetailPage /> },
            {
              path: "inventory/accounts-payable",
              element: <SupplierInvoicesPage />,
            },
            {
              path: "inventory/debit-notes",
              element: <DebitNotesPage />,
            },
            {
              path: "inventory/debit-notes/new",
              element: <DebitNoteCreatePage />,
            },
            {
              path: "inventory/debit-notes/:id",
              element: <DebitNotesPage />,
            },
            {
              path: "inventory/reorder-forecast",
              element: <PredictiveReorderPage />,
            },
          ],
        },
        // GYM-592: Equipment asset register
        {
          element: <MaintenanceGuard />,
          children: [
            {
              path: "maintenance",
              element: <Navigate to="/maintenance/assets" replace />,
            },
            { path: "maintenance/assets", element: <AssetRegisterPage /> },
            {
              path: "maintenance/assets/import",
              element: <AssetCsvImportPage />,
            },
            { path: "maintenance/assets/new", element: <AssetFormPage /> },
            {
              path: "maintenance/assets/:assetId",
              element: <AssetDetailPage />,
            },
            {
              path: "maintenance/assets/:assetId/edit",
              element: <AssetFormPage />,
            },
            // GYM-1438: Facility zones
            { path: "maintenance/zones", element: <ZoneListPage /> },
            { path: "maintenance/zones/new", element: <ZoneFormPage /> },
            {
              path: "maintenance/zones/:zoneId/edit",
              element: <ZoneFormPage />,
            },
          ],
        },
        // GYM-603i: Access devices + live feed (legacy)
        { path: "settings/access-devices", element: <DevicesPage /> },
        // GYM-695: BioSec device management
        { path: "settings/devices", element: <BioDevicesPage /> },
        { path: "settings/devices/:id", element: <BioDeviceDetailPage /> },
        // GYM-1620: Access Zones
        { path: "settings/access-zones", element: <AccessZonesPage /> },
        // GYM-1424: Kiosk device management
        { path: "settings/kiosk-devices", element: <KioskDevicesPage /> },
        // GYM-697: BioSec live reception feed
        { path: "check-in/live", element: <LiveFeedPage /> },
        // GYM-699: BioSec attendance & footfall analytics
        { path: "analytics/access", element: <AccessAnalyticsPage /> },
        // GYM-533: 2FA management (authenticated)
        { path: "settings/security/2fa", element: <TwoFactorSettingsPage /> },
        {
          path: "settings/security/2fa/enrol",
          element: <TwoFactorEnrolPage />,
        },
        {
          path: "settings/security/2fa/recovery",
          element: <RecoveryCodesPage />,
        },
        // GYM-1499: 2FA reset approval queue
        {
          path: "settings/security/2fa/reset-queue",
          element: <TwoFactorResetQueuePage />,
        },
        // GYM-1430: tenant security policy (mandatory-2FA roles + trusted-device window)
        { path: "settings/security/policy", element: <SecurityPolicyPage /> },
        // GYM-501: Exercise Library
        { path: "exercises", element: <ExerciseLibraryPage /> },
        { path: "exercises/:id", element: <ExerciseDetailPage /> },
        // GYM-502: Workout Templates
        { path: "workouts", element: <WorkoutListPage /> },
        { path: "workouts/new", element: <WorkoutBuilderPage /> },
        { path: "workouts/:id", element: <WorkoutBuilderPage /> },
        // GYM-503: Training Programs
        { path: "programs", element: <ProgramListPage /> },
        { path: "programs/assign", element: <AssignProgramPage /> },
        { path: "programs/new", element: <ProgramBuilderPage /> },
        { path: "programs/:id", element: <ProgramBuilderPage /> },
        { path: "programs/clients", element: <ClientsProgramsPage /> },
        // Nutrition & meal planning
        { path: "nutrition", element: <Navigate to="/nutrition/plans" replace /> },
        { path: "nutrition/plans", element: <NutritionPlanListPage /> },
        { path: "nutrition/plans/new", element: <NutritionPlanBuilderPage /> },
        { path: "nutrition/plans/:id", element: <NutritionPlanBuilderPage /> },
        { path: "nutrition/foods", element: <FoodListPage /> },
        { path: "nutrition/meals", element: <MealTemplatesPage /> },
        // GYM-509/510/1160: Leads module + pipeline board + CRM settings + CSV import
        {
          element: <PermissionGuard permission="crm.lead.read" />,
          children: [
            { path: "leads", element: <LeadsListPage /> },
            { path: "leads/board", element: <LeadBoardPage /> },
            // GYM-1561: trials worklist page
            { path: "leads/trials", element: <TrialsWorklistPage /> },
            { path: "leads/:leadId", element: <LeadDetailPage /> },
            { path: "settings/crm", element: <LeadCrmSettingsPage /> },
            // GYM-511: Trial templates
            { path: "settings/trial-templates", element: <TrialTemplatesPage /> },
          ],
        },
        // GYM-539: Daily owner digest settings
        { path: "getting-started", element: <GettingStartedPage /> },
        { path: "settings/digest", element: <DigestSettingsPage /> },
        { path: "settings/payment-gateways", element: <PaymentGatewaysPage /> },
        // GYM-1284: Accounting GL mapping settings
        { path: "settings/accounting", element: <AccountingMappingPage /> },
        // GYM-1433: Locked accounting periods + maker-checker unlock
        { path: "settings/accounting-periods", element: <LockedAccountingPeriodsPage /> },
        // GYM-512: Referral program settings
        {
          path: "settings/referral-program",
          element: <ReferralProgramSettingsPage />,
        },
        // GYM-1073.5: Member document visibility window
        {
          path: "settings/member-document-visibility",
          element: <DocumentVisibilitySettingsPage />,
        },
        // GYM-600: Developer portal (API keys + webhooks)
        { path: "settings/developer", element: <DeveloperPortalPage /> },
        { path: "settings/about", element: <AboutPage /> },
        // GYM-596: Tenant white-label branding settings
        { path: "settings/branding", element: <BrandingSettingsPage /> },
        // GYM-1426: Induction template management
        {
          path: "settings/induction-template",
          element: <InductionTemplatePage />,
        },
        // GYM-1753: Assessment template management
        {
          path: "settings/assessment-template",
          element: <AssessmentTemplatePage />,
        },
        // GYM-1538: Notification template management
        {
          path: "settings/notification-templates",
          element: <NotificationTemplatesPage />,
        },
        // Email / SMS / WhatsApp provider settings screens
        { path: "settings/notifications/email", element: <EmailSettingsPage /> },
        { path: "settings/notifications/sms", element: <SmsSettingsPage /> },
        { path: "settings/notifications/whatsapp", element: <WhatsAppSettingsPage /> },
        // GYM-1249: Top referrers leaderboard
        { path: "referrals/leaderboard", element: <ReferralLeaderboardPage /> },
        // GYM-1440: Referral admin (budget, ledger, fraud, clawback)
        { path: "referrals/admin", element: <ReferralAdminPage /> },
        // GYM-1427: Badge catalog (settings area) + member-streak leaderboard (staff)
        { path: "settings/badge-catalog", element: <BadgeCatalogPage /> },
        // GYM-1429: Tenant operational/financial settings screens
        {
          path: "settings/finance/access-dues",
          element: <AccessDuesSettingsPage />,
        },
        {
          path: "settings/finance/inventory-cogs",
          element: <InventoryCOGSSettingsPage />,
        },
        {
          path: "settings/finance/pt-commission",
          element: <PTCommissionSettingsPage />,
        },
        {
          path: "settings/pt-reassign",
          element: <PtReassignSettingsPage />,
        },
        {
          path: "settings/finance/reconciliation-pos",
          element: <ReconciliationPOSSettingsPage />,
        },
        {
          path: "settings/finance/discount-limits",
          element: <DiscountLimitsSettingsPage />,
        },
        { path: "settings/ops/community", element: <CommunitySettingsPage /> },
        {
          path: "settings/ops/member-policy",
          element: <MemberPolicySettingsPage />,
        },
        {
          path: "settings/ops/localization",
          element: <LocalizationSettingsPage />,
        },
        {
          path: "settings/self-registration",
          element: <SelfRegistrationSettingsPage />,
        },
        { path: "settings/ops/approvals", element: <ApprovalSettingsPage /> },
        { path: "gamification/leaderboard", element: <GamifLeaderboardPage /> },
      ],
    },
    // GYM-760: member portal (separate auth surface from the staff console).
    { path: "/member/login", element: <MemberLoginPage /> },
    { path: "/member/forgot-password", element: <MemberForgotPasswordPage /> },
    { path: "/member/set-password", element: <MemberSetPasswordPage /> },
    {
      path: "/member",
      element: (
        <MemberProtectedRoute>
          <MemberPortalLayout />
        </MemberProtectedRoute>
      ),
      children: [
        { index: true, element: <MemberHomePage /> },
        { path: "classes", element: <MemberClassesPage /> },
        { path: "workout", element: <MemberWorkoutPage /> },
        { path: "nutrition", element: <MemberNutritionPage /> },
        { path: "documents", element: <MemberDocumentsPage /> },
        { path: "account", element: <MemberPrivacyPage /> },
        { path: "consent", element: <MemberConsentPage /> },
        // GYM-1427: Achievements tab (badges + streak + leaderboard)
        { path: "achievements", element: <MemberAchievementsPage /> },
        // GYM-1693: Door access QR tab
        { path: "access-qr", element: <MemberAccessQrPage /> },
        { path: "pt", element: <MemberPtPage /> },
        { path: "wallet", element: <MemberWalletPage /> },
      ],
    },
    // GYM-1685: gym-picker — public, standalone pre-theming shell
    { path: "/gym", element: <GymPickerPage /> },
    { path: "/join", element: <GymPickerPage /> },
    // GYM-1680: self-registration wizard — public, no auth
    { path: "/register", element: <SelfRegWizardPage /> },
    // GYM-1755: self-service trial booking — public, token in path
    { path: "/trial/:token", element: <TrialBookingPage /> },
    // GYM-515: NPS survey — no auth, standalone micro-page
    { path: "/survey/nps/:token", element: <NpsSurveyPage /> },
    // GYM-498: kiosk classes display — no auth, no AppShell, full-screen
    { path: "/kiosk/classes/:branchId", element: <KioskClassesPage /> },
    // GYM-604: self-service kiosk wizard — device-token auth, full-screen
    { path: "/kiosk", element: <KioskShell /> },
    {
      path: "/kiosk/payment-return/:sessionId",
      element: <KioskPaymentReturnPage />,
    },
    { path: "*", element: <Navigate to="/" replace /> },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);
